<template>
    <Loading v-if="loading"/>
    <Page>
        <Container>
            <div class="mx-auto max-w-sm" @click="noSleepEnable">

                <div class="flex items-center justify-between mb-6">
                    <Title>{{pageTitle}}</Title>
                    <div class="cursor-pointer" @click="handleCancel">
                        <Icon icon="times" class="text-2xl"/>
                    </div>
                </div>

                <div v-if="showForm" class="flex items-center justify-around w-full bg-neutral-100 dark:bg-neutral-800 rounded px-4 py-4 mb-6">
                    <div class="cursor-pointer w-10 h-10 rounded flex items-center justify-center border-2 transition-colors"
                         :class="showDate ? 'border-brand-primary-400 text-brand-primary-400' : 'border-neutral-300 dark:border-neutral-500 text-neutral-500 dark:text-neutral-400'"
                         @click="showDate = !showDate"
                         title="Date">
                        <Icon icon="calendar" class="text-lg"/>
                    </div>
                    <div v-if="user.settings.workoutTimer"
                         class="cursor-pointer w-10 h-10 rounded flex items-center justify-center border-2 transition-colors"
                         :class="showTimer ? 'border-brand-primary-400 text-brand-primary-400' : 'border-neutral-300 dark:border-neutral-500 text-neutral-500 dark:text-neutral-400'"
                         @click="showTimer = !showTimer"
                         title="Timer">
                        <Icon icon="stopwatch" class="text-lg"/>
                    </div>
                    <div v-if="user.settings.workoutProgressPhoto"
                         class="cursor-pointer w-10 h-10 rounded flex items-center justify-center border-2 transition-colors"
                         :class="showProgressPhoto ? 'border-brand-primary-400 text-brand-primary-400' : 'border-neutral-300 dark:border-neutral-500 text-neutral-500 dark:text-neutral-400'"
                         @click="handleProgressPhotoToggle"
                         title="Progress Photo">
                        <Icon icon="camera" class="text-lg"/>
                    </div>
                    <div v-if="user.settings.workoutNotes"
                         data-cy="toggle-workout-notes"
                         class="cursor-pointer w-10 h-10 rounded flex items-center justify-center border-2 transition-colors"
                         :class="showNotes ? 'border-brand-primary-400 text-brand-primary-400' : 'border-neutral-300 dark:border-neutral-500 text-neutral-500 dark:text-neutral-400'"
                         @click="showNotes = !showNotes"
                         title="Notes">
                        <Icon icon="sticky-note" class="text-lg"/>
                    </div>
                    <div v-if="workout"
                         data-cy="delete-workout"
                         class="cursor-pointer w-10 h-10 rounded flex items-center justify-center border-2 transition-colors border-red-500 text-red-500 hover:border-red-600 hover:text-red-600"
                         @click="handleDelete"
                         title="Delete">
                        <Icon icon="trash-alt" class="text-lg"/>
                    </div>
                </div>

                <div v-if="error">
                    <Instruction type="error">
                        There's been an error.
                    </Instruction>

                    <Button primary block @click="handleReload">
                        Reload App
                    </Button>
                </div>

                <template v-else>

                    <div v-if="!format && !loading">
                        <Button primary outlined block @click="handleFormatSet('blank')">
                            Blank
                        </Button>
                        <div class="text-center my-3">
                            - or -
                        </div>
                        <Label>Templates</Label>
                        <template v-for="template in templates">
                            <Button primary outlined block @click="handleTemplateSet(template)" class="my-3">
                                {{ template.name }}
                            </Button>
                        </template>
                    </div>

                    <div v-show="showForm">

                        <Field v-show="showDate">
                            <Label>Date</Label>
                            <DateTimeInput v-model="form.fields.workoutAt"/>
                        </Field>

                        <Field v-if="user.settings.workoutTimer && showTimer">
                            <Label>Timer</Label>
                            <Timer v-model="form.fields.duration"/>
                        </Field>

                        <Field v-if="template">
                            <Label>Template</Label>
                            <p class="font-bold">{{ template.name }}</p>
                        </Field>


                        <Field>
                            <ExercisesInput v-model="form.fields.exercises"
                                            :sorting="sorting"
                                            @toggle="sorting = !sorting"
                            />
                        </Field>

                        <Field v-if="user.settings.workoutProgressPhoto && showProgressPhoto">
                            <Label>Progress Photo</Label>
                            <FileInput ref="fileInput" v-model="form.fields.fileId" block/>
                        </Field>

                        <Field v-if="user.settings.workoutNotes && showNotes">
                            <Label>Notes</Label>
                            <TextareaInput v-model="form.fields.notes"/>
                        </Field>

                        <Controls>

                            <template v-slot:left>
                            </template>

                            <template v-slot:right>
                                <div class="flex items-center justify-between gap-4">
                                    <Button primary @click="handleSave(false)">
                                        Save
                                    </Button>
                                </div>
                            </template>
                        </Controls>

                    </div>

                </template>

            </div>
        </Container>
    </Page>
</template>

<script>
import {defineComponent} from 'vue';
import {
  mapActions,
} from 'vuex';
import {Form} from '@/internal.js';
import Timer from '@/components/Timer.vue';
import moment from 'moment-timezone';
import Row from '@/components/Exercise/Row.vue';
import {parseBoolean, uuid} from '@/helpers.js';
import ExercisesInput from '@/components/ExercisesInput.vue';
import NoSleep from 'nosleep.js';
import Subtitle from '@/components/Subtitle.vue';
import Instruction from '@/components/Instruction.vue';
import {capturePostHogEvent} from '@/utils/posthog';

const defaultParams = {
  include: [
    'exercises',
    'file',
    'template',
  ],
};

export default defineComponent({
  name: 'Form',
  components: {
    Instruction,
    Subtitle,
    ExercisesInput,
    Row,
    Timer,
  },
  computed: {
    bands() {
      return this.$store.getters['band/all'] || [];
    },
    movements() {
      return this.$store.getters['movement/all'] || [];
    },
    pageTitle() {
      return this.workout ? 'Edit Workout' : 'New Workout';
    },
    showForm() {

      if (this.loading) {
        return false;
      }

      return this.format === 'blank' || this.format === 'template' && this.template;
    },
    template() {
      return this.form.fields.templateId ? this.templates.find(template => template.id === this.form.fields.templateId) : null;
    },
    templateMovements() {

      let movements = this.template.movements || [];

      return this.template ? this.template.movementRank.map((item) => {

        let movement = {
          ...movements.find(movement => movement.id === item.id),
        };

        movement.rank = item.rank;
        return movement;
      }) : [];
    },
    templates() {
      return this.$store.getters['template/all'].sort((a, b) => a.name > b.name ? 1 : -1) || [];
    },
    timezone() {
      return this.user.timezone || moment.tz.guess();
    },
    user() {
      return this.$store.getters['auth/user'];
    },
    workout() {
      return this.$route.params.workoutUUID ? this.$store.getters['workout/findBy'](this.$route.params.workoutUUID, 'uuid') : null;
    },
  },
  async created() {

    await this.loadMovements();
    await this.loadBands();
    await this.loadTemplates();

    // Initialize visibility - default to hidden unless setting indicates it should be open
    this.showDate = false;
    this.showTimer = false;
    this.showProgressPhoto = false;
    this.showNotes = false;

    if (this.$route.params.workoutUUID) {
      this.format = 'blank';
      this.loadWorkout();
    } else {
      this.form.fields.workoutAt = moment.utc().tz(this.timezone).format('YYYY-MM-DDTHH:mm');
    }
  },
  destroyed() {
    this.noSleepDisable();
  },
  data() {
    return {
      autoSaving: false,
      error: null,
      format: null,
      form: new Form({
        duration: null,
        exercises: [],
        fileId: null,
        notes: '',
        templateId: null,
        workoutAt: null,
      }),
      loading: false,
      noSleep: new NoSleep(),
      showDate: false,
      showNotes: false,
      showProgressPhoto: false,
      showTimer: false,
      sorting: false,
    };
  },
  methods: {
    ...mapActions({
      bandIndex: 'band/index',
      movementIndex: 'movement/index',
      templateIndex: 'template/index',
      templateShow: 'template/show',
      workoutDestroy: 'workout/destroy',
      workoutShow: 'workout/show',
      workoutStore: 'workout/store',
      workoutUpdate: 'workout/update',
    }),
    fillForm() {

      this.form.fields = {
        duration: this.workout.duration,
        exercises: [
          ...this.workout.exercises.map((exercise) => {

            if (exercise.bands) {
              exercise.bandIds = exercise.bands.map(band => band.id);
            }

            return exercise;
          }),
        ],
        fileId: this.workout.file ? this.workout.file.id : null,
        notes: this.workout.notes,
        templateId: this.workout.template ? this.workout.template.id : null,
        workoutAt: moment(this.workout.workoutAt).tz(this.timezone).format('YYYY-MM-DDTHH:mm'),
      };

      // Show fields if they have values
      if (this.workout.notes && this.workout.notes.length > 0) {
        this.showNotes = true;
      }
      if (this.workout.file) {
        this.showProgressPhoto = true;
      }
      if (this.workout.duration && this.workout.duration > 0) {
        this.showTimer = true;
      }
    },
    async handleCancel() {

      let abort = false;

      if (this.workout) {
        if (this.shouldDiscardAutosavedWorkout()) {
          await this.workoutDestroy({
            id: this.workout.uuid,
          }).catch((error) => {
            abort = true;
          });
        } else {
          await this.handleSave(false).catch((error) => {
            abort = true;
          });
        }
      }

      if (abort) {
        return;
      }

      this.noSleepDisable();
      this.$router.push(window.app.findRouteByName('workout/index'));
    },
    handleDelete() {
      window.app.dialog({
        title: 'Delete Workout',
        message: 'Are you sure you want to delete this workout?',
        actions: [
          {
            text: 'No',
            outlined: true,
            click: () => {
            },
          },
          {
            text: 'Yes',
            danger: true,
            click: () => {

              this.loading = true;
              this.workoutDestroy({
                id: this.workout.uuid,
              }).then(() => {
                capturePostHogEvent('workout_deleted');
                window.app.snackbar('Workout Deleted');
                this.selectedFile = null;
                this.noSleepDisable();
                this.$router.push(window.app.findRouteByName('workout/index'));
              }).finally(() => {
                this.loading = false;
              });

            },
          }
        ],
      });
    },
    handleFormatSet(format) {
      this.format = format;
    },
    handleProgressPhotoToggle() {
      const wasHidden = !this.showProgressPhoto;
      this.showProgressPhoto = !this.showProgressPhoto;

      // If toggling on and no file exists, launch file picker
      if (wasHidden && !this.form.fields.fileId) {
        this.$nextTick(() => {
          if (this.$refs.fileInput) {
            this.$refs.fileInput.handleFinderLaunch();
          }
        });
      }
    },
    exhaustionLevelSummary() {
      const exercises = this.form.fields.exercises || [];
      const exercisesWithExhaustionLevel = exercises.filter((exercise) => {
        return exercise.exhaustionLevel !== undefined && exercise.exhaustionLevel !== null;
      });

      return {
        has_exhaustion_level: exercisesWithExhaustionLevel.length > 0,
        exercises_with_exhaustion_level_count: exercisesWithExhaustionLevel.length,
        exercises_without_exhaustion_level_count: exercises.length - exercisesWithExhaustionLevel.length,
      };
    },
    handleReload() {
      this.loading = true;
      window.location.replace('/workout');
    },
    async handleSave(autoSaved = false, withZeroReps = false) {

      if (this.loading || this.autoSaving) {
        return false;
      }

      let abort = false;

      if (!autoSaved && this.user.settings.warnZeroReps && !withZeroReps) {

        if (this.shouldWarnZeroReps()) {

          await new Promise((resolve, reject) => {

            window.app.dialog({
              title: 'Warning: Zero Reps',
              message: 'Are you sure you want to save this workout with zero reps?',
              actions: [
                {
                  text: 'No',
                  outlined: true,
                  click: () => {
                    reject();
                  },
                },
                {
                  text: 'Save Anyway',
                  warning: true,
                  click: () => {
                    resolve();
                  },
                }
              ],
            });

          }).catch((error) => {
            abort = true;
          });
        }
      }

      if (abort) {
        return new Promise((resolve, reject) => {
          reject();
        });
      }

      let action;
      let payload = {
        ...this.form.fields,
        params: {
          ...defaultParams,
        },
        autoSaved,
        workoutAt: moment.tz(this.form.fields.workoutAt, this.timezone).utc().toISOString(),
      };

      if (this.workout) {
        action = this.workoutUpdate;
        payload.id = this.workout.uuid;
      } else {
        action = this.workoutStore;
      }

      this.autoSaving = autoSaved;
      this.loading = true;

      return action(payload).then((id) => {

        if (autoSaved) {
          if (!this.$route.params.workoutUUID) {
            const workout = this.$store.getters['workout/findBy'](id, 'id');
            this.$router.push(`/workout/${workout.uuid}/edit`);
          } else {
            this.loading = false;
            return this.loadWorkout(true);
          }

        } else {

          const exhaustionLevelSummary = this.exhaustionLevelSummary();

          capturePostHogEvent('workout_saved', {
            exercise_count: this.form.fields.exercises.length,
            exercise_exhaustion_level_enabled: this.user.settings.exerciseExhaustionLevel,
            ...exhaustionLevelSummary,
            used_template: !!this.form.fields.templateId,
            is_new: !this.workout,
          });

          window.app.snackbar({
            type: 'success',
            message: 'Workout Saved',
          });

          this.noSleepDisable();
          this.$router.push(window.app.findRouteByName('workout/index'));

          window.dispatchEvent(new CustomEvent('v3me:survey-prompt', {
            detail: {
              manual: false,
              promptLocation: 'workout_saved',
            },
          }));
        }

      }).catch((error) => {
        console.error(error);
        this.form.recordErrors(error);
        window.app.snackbarError(error);
      }).finally(() => {
        this.autoSaving = false;
        this.loading = false;
      });
    },
    handleTemplateSet(template) {

      this.form.fields.templateId = template.id;

      let exercises = [];

      this.templateMovements.map((movement => {
        exercises.push({
          id: uuid(),
          bandIds: [],
          movementId: movement.id,
          rank: movement.rank,
        });
      }));

      this.form.fields.exercises = [
        ...exercises.sort((a, b) => a.rank > b.rank ? 1 : -1)
      ];
    },
    loadBands() {
      this.loading = true;
      return this.bandIndex().finally(() => {
        this.loading = false;
      });
    },
    loadMovements() {
      this.loading = true;
      return this.movementIndex().finally(() => {
        this.loading = false;
      });
    },
    loadTemplates() {
      this.loading = true;
      return this.templateIndex({
        params: {
          include: [
            'movements',
          ],
        },
      }).finally(() => {
        this.loading = false;
      });
    },
    loadWorkout(inBackground = false) {

      if (this.loading) {
        return;
      }

      this.loading = !inBackground;

      return this.workoutShow({
        id: this.$route.params.workoutUUID,
        params: {
          ...defaultParams,
        },
      }).then(this.fillForm).catch((error) => {

        if (error.response.status === 403) {

          window.app.snackbar({
            message: 'Error: Reloading App',
            type: 'error',
          });

          setTimeout(() => {
            window.location.replace('/workout');
          }, 1000);

        } else {
          console.error(error);
        }

        this.error = error;

      }).finally(() => {
        this.loading = false;
      })
    },
    noSleepDisable() {
      if (this.noSleep.isEnabled) {
        try {
          this.noSleep.disable();
        } catch (error) {
        }
      }
    },
    async noSleepEnable() {
      if (!this.noSleep.isEnabled) {
        try {
          await this.noSleep.enable();
        } catch (error) {
        }
      }
    },
    shouldDiscardAutosavedWorkout() {
      return !!(this.workout && this.workout.autoSaved && !this.workoutHasLoggedContent());
    },
    workoutHasLoggedContent() {
      const fields = this.form.fields;

      if (this.hasPositiveValue(fields.duration) || this.hasValue(fields.fileId) || this.hasText(fields.notes)) {
        return true;
      }

      return (fields.exercises || []).some((exercise) => this.exerciseHasLoggedContent(exercise));
    },
    exerciseHasLoggedContent(exercise) {
      return this.hasPositiveValue(exercise.duration)
        || this.hasPositiveValue(exercise.repetitions)
        || this.hasPositiveValue(exercise.partialRepetitions)
        || this.hasPositiveValue(exercise.weight)
        || this.hasValue(exercise.exhaustionLevel)
        || this.hasText(exercise.notes)
        || this.hasArrayValue(exercise.bandIds);
    },
    hasPositiveValue(value) {
      return Number(value || 0) > 0;
    },
    hasText(value) {
      return typeof value === 'string' && value.trim().length > 0;
    },
    hasValue(value) {
      return value !== undefined && value !== null && value !== '';
    },
    hasArrayValue(value) {
      return Array.isArray(value) && value.length > 0;
    },
    movementForExercise(exercise) {
      if (exercise.movement) {
        return exercise.movement;
      }

      return this.movements.find((movement) => {
        return movement.id === exercise.movementId;
      }) || null;
    },
    movementTracksRepetitions(exercise) {
      return parseBoolean(this.movementForExercise(exercise)?.trackRepetitions);
    },
    shouldWarnZeroReps() {
      const exercises = this.form.fields.exercises || [];
      const loggedExercises = exercises.filter((exercise) => this.exerciseHasLoggedContent(exercise));
      const exercisesToCheck = loggedExercises.length ? loggedExercises : exercises;
      const exercisesTrackingReps = exercisesToCheck.filter((exercise) => this.movementTracksRepetitions(exercise));

      return exercisesTrackingReps.length > 0 && !exercisesTrackingReps.some((exercise) => {
        return this.hasPositiveValue(exercise.repetitions) || this.hasPositiveValue(exercise.partialRepetitions);
      });
    },
  },
  watch: {
    ['form.fields.exercises']: {
      deep: true,
      handler() {
        this.handleSave(true);
      },
    },
    format: {
      async handler() {
        if (this.format === 'blank') {
          this.handleSave(true);
        }
      },
    }
  }
})
</script>

<style scoped lang="scss">

</style>
